'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import { useConversationOptimized as useConversation } from '@/hooks/useConversationOptimized'
import { useOpenAIRealtime } from '@/hooks/useOpenAIRealtime'
import { VoiceSettingsModal } from '@/components/conversation/VoiceSettingsModal'
import { AttachmentsModal } from '@/components/conversation/AttachmentsModal'
import FollowUpButtons from '@/components/conversation/FollowUpButtons'
import { ProductResults } from '@/components/conversation/ProductResults'
import SubscriptionUpsellModal from '@/components/conversation/SubscriptionUpsellModal'
import QuizModal from '@/components/conversation/QuizModal'
import { useOnboarding } from '@/hooks/useOnboarding'
import { OnboardingTour } from '@/components/onboarding/OnboardingTour'
import { HelpButton } from '@/components/onboarding/HelpButton'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import ConversationsSidebar from '@/components/conversation/ConversationsSidebar'
import { Category } from '@/types/database'
import { FollowUpOption } from '@/utils/ai/types'
import { Mic, Send, MessageCircle, X, Copy, VolumeX, Settings, Paperclip, BookOpen, Menu } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { uploadFileToStorage, saveAttachmentToDatabase, addToUserFileLibrary, getSmartCategoryFromContext } from '@/utils/fileUpload'
import LanguagePicker from '@/components/ui/LanguagePicker'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// ─── DESIGN TOKENS (matches Sage system) ──────────────────────────────
const S = {
  sage: '#4a7c5e',
  sageDark: '#2d5a3d',
  sageLight: '#e8f2ec',
  sageMid: '#b8d9c4',
  cream: '#faf7f2',
  creamDark: '#f0ebe0',
  navy: '#1a2740',
  slate: '#4a5568',
  gold: '#c9903a',
  white: '#ffffff',
  studyBlue: '#3b5bdb',
  fontDisplay: "'Playfair Display', Georgia, serif",
  fontBody: "'Source Serif 4', Georgia, serif",
}

// ─── Extended category type with education fields ──────────────────────
type CategoryWithEdu = Category & {
  is_educational?: boolean
}

export default function CategoryPage() {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const mode = searchParams.get('mode')
  const { user, userProfile, isLoading: authLoading } = useAuth()
  const { isAdmin } = useAdmin()
  const [category, setCategory] = useState<CategoryWithEdu | null>(null)
  const [isCategoryLoading, setIsCategoryLoading] = useState<boolean>(false)
  const [newMessage, setNewMessage] = useState('')
  const [error, setError] = useState('')
  const [selectedImages, setSelectedImages] = useState<string[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ url: string; path: string; name: string; type: string; size: number }>>([])
  const [showUpsellModal, setShowUpsellModal] = useState(false)
  const [showVoiceSettings, setShowVoiceSettings] = useState(false)
  const [showVoiceControl, setShowVoiceControl] = useState(false)
  const [showAttachments, setShowAttachments] = useState(false)
  const [limitInfo, setLimitInfo] = useState<{ currentCount: number; maxAllowed: number } | null>(null)
  const [realtimeVoice, setRealtimeVoice] = useState<string>('alloy')

  // ── Education-specific state ─────────────────────────────────────────
  // When is_educational=true we show a "Start Learning Journey" prompt
  // before launching a conversation, unless the user is already a student
  // (facility_role='member') in which case we redirect straight to /learn/:categorySlug
  // Sidebar state as requested by user
  const [showSidebar, setShowSidebar] = useState(false)
  const [showEduChoice, setShowEduChoice] = useState(false)

  // Sidebar responsive init
  useEffect(() => {
    setShowSidebar(window.innerWidth >= 1024)
  }, [])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const hasStartedRef = useRef<boolean>(false)
  const hasScrolledToBottomRef = useRef(false)
  const [voiceFollowUpOptions, setVoiceFollowUpOptions] = useState<FollowUpOption[]>([])
  const { t } = useTranslation()

  const { showTour, currentStep, totalSteps, hasCompletedTour, nextStep, previousStep, skipTour, restartTour, currentStepData } = useOnboarding()

  const { conversation, messages, followUpOptions, productResults, isLoading, sendMessage, startNewConversation, refreshMessages, quiz, isQuizOpen, setIsQuizOpen, submitQuizResults } = useConversation()

  const { isConnected: isVoiceConnected, isListening: isVoiceListening, isSpeaking: isVoiceSpeaking, isMuted, toggleMute, currentTranscript, error: voiceError, stopVoiceSession, toggleVoiceSession, clearError: clearVoiceError, updateRealtimeVoice } = useOpenAIRealtime(conversation?.id, realtimeVoice)

  const isVoiceProcessing = isVoiceConnected && !isVoiceListening && !isVoiceSpeaking

  const handleRefreshAndScroll = useCallback(async () => {
    await refreshMessages()
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }, [refreshMessages])

  const handleSwitchToTextMode = useCallback(async () => {
    stopVoiceSession()
    await new Promise(r => setTimeout(r, 500))
    await handleRefreshAndScroll()
  }, [stopVoiceSession, handleRefreshAndScroll])

  useEffect(() => {
    if (user?.user_metadata?.preferred_voice) setRealtimeVoice(user.user_metadata.preferred_voice)
  }, [user])

  const handleRealtimeVoiceChange = useCallback(async (voice: string) => {
    setRealtimeVoice(voice)
    if (user) {
      try {
        const currentPrefs = (userProfile?.preferences as Record<string, unknown> | null) || {}
        await supabase.from('users').update({ preferences: { ...currentPrefs, preferred_voice: voice }, updated_at: new Date().toISOString() }).eq('id', user.id).single()
      } catch { }
    }
    try { await updateRealtimeVoice(voice) } catch { }
  }, [user, userProfile, updateRealtimeVoice])

  useEffect(() => { setShowVoiceControl(isVoiceListening || isVoiceSpeaking) }, [isVoiceListening, isVoiceSpeaking])

  useEffect(() => {
    if (!conversation?.id) return
    if (isVoiceConnected && !isVoiceListening && !isVoiceSpeaking) {
      setTimeout(() => handleRefreshAndScroll(), 200)
    }
  }, [isVoiceConnected, isVoiceListening, isVoiceSpeaking, conversation?.id, handleRefreshAndScroll])

  useEffect(() => {
    if (!conversation?.id) return
    if (!isVoiceConnected && !isVoiceListening && !isVoiceSpeaking) {
      setTimeout(() => handleRefreshAndScroll(), 500)
    }
  }, [isVoiceConnected, isVoiceListening, isVoiceSpeaking, conversation?.id, handleRefreshAndScroll])

  useEffect(() => {
    if (!conversation?.id) return
    const handler = (e: CustomEvent) => {
      if (e.detail.conversationId === conversation.id) setTimeout(() => handleRefreshAndScroll(), 100)
    }
    window.addEventListener('voiceMessageSaved', handler as EventListener)
    return () => window.removeEventListener('voiceMessageSaved', handler as EventListener)
  }, [conversation?.id, handleRefreshAndScroll])

  useEffect(() => {
    const handler = (e: Event) => {
      const { conversationId: cid, options } = (e as CustomEvent).detail
      if (conversation?.id && cid === conversation.id) setVoiceFollowUpOptions(options)
    }
    window.addEventListener('voiceOptionsReceived', handler as EventListener)
    return () => window.removeEventListener('voiceOptionsReceived', handler as EventListener)
  }, [conversation?.id])

  useEffect(() => { if (!isVoiceConnected) setVoiceFollowUpOptions([]) }, [isVoiceConnected])

  useEffect(() => {
    if (messages.length > 0 && !hasScrolledToBottomRef.current && !isLoading) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
        hasScrolledToBottomRef.current = true
      }, 100)
    }
  }, [messages.length, isLoading])

  const adjustTextareaHeight = () => {
    const ta = textareaRef.current
    if (ta) { ta.style.height = 'auto'; ta.style.height = `${Math.min(ta.scrollHeight, 120)}px` }
  }

  const fetchCategory = useCallback(async () => {
    try {
      setIsCategoryLoading(true); setError('')
      const slug = String(params.slug || '')
      const slugify = (name: string) => name.toLowerCase().replace(/&/g, 'and').replace(/[\/]+/g, '-').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')

      // ── Phase 5.2: fetch is_educational along with other fields ──────────
      let query = supabase
        .from('categories')
        .select('id,name,icon,description,is_visible,is_educational,educational_level')
        .order('name')
      if (!isAdmin) query = query.eq('is_visible', true)

      const { data: allCategories, error: categoriesError } = await query
      if (categoriesError) { setError(t('category.loadError')); return }

      const match = (allCategories || []).find(c => slugify(c.name) === slug)
      if (!match) { setError(t('category.notFound')); return }

      const cat = match as unknown as CategoryWithEdu
      setCategory(cat)

      // ── Education branch ──────────────────────────────────────────────────
      if (cat.is_educational && mode !== 'learn' && mode !== 'casual') {
        const role = userProfile?.facility_role
        const facilityId = userProfile?.facility_id

        if (role === 'member' && facilityId) {
          // Enrolled student → go straight to their specific portal
          if (userProfile?.facility_type === 'church') {
            navigate('/church/learn', { replace: true })
          } else {
            navigate(`/learn?category=${cat.id}`, { replace: true })
          }
          return
        }

        // Non-student visiting an educational category:
        // Show the "Start Learning Journey" choice screen rather than
        // launching a conversation immediately.
        setShowEduChoice(true)
        setIsCategoryLoading(false)
        return
      }

      // ── Education branch: Handling Start ─────────────────────────────────
      if (cat.is_educational && mode === 'learn') {
        if (userProfile?.facility_type === 'church') {
          navigate('/church/learn', { replace: true })
        } else {
          navigate(`/learn/session?category=${cat.id}`, { replace: true })
        }
        return
      }
      // ── Standard companion branch (unchanged) ─────────────────────────────
      if (user && !hasStartedRef.current && messages.length === 0) {
        hasStartedRef.current = true
        const result = await startNewConversation(cat.id, user.id)

        if (result && typeof result === 'object' && 'error' in result && result.error === 'CONVERSATION_LIMIT_REACHED') {
          setLimitInfo(result.limitInfo as { currentCount: number; maxAllowed: number })
          setShowUpsellModal(true)
          hasStartedRef.current = false
        } else if (result && typeof result === 'object' && 'id' in result) {
          const conversationId = (result as { id: string }).id
          navigate(`/conversation/${conversationId}`, { replace: true })
          return
        }
      }
    } catch { setError(t('category.loadError')) }
    finally { setIsCategoryLoading(false) }
  }, [params.slug, params.lang, user, userProfile, isAdmin, messages.length, navigate, startNewConversation, t, mode])

  const abortControllerRef = useRef<AbortController | null>(null)
  const hasFetchedRef = useRef<boolean>(false)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    if (user && !authLoading && !category && !isCategoryLoading && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      fetchCategory()
    }
    return () => { abortControllerRef.current?.abort() }
  }, [user, authLoading, category, isCategoryLoading, fetchCategory])

  useEffect(() => {
    if (category && user && !category.is_educational) {
      supabase.functions.invoke('ai-conversation', { body: { warmup: true } }).catch(() => { })
    }
  }, [category, user])

  const handleFollowUpSelect = async (option: FollowUpOption, customInput?: string) => {
    const msg = customInput || option.text
    let payload = null
    if ((option as any).payload && typeof (option as any).payload === 'string') {
      try { payload = JSON.parse((option as any).payload) } catch { }
    }
    await sendMessage(msg, true, [], payload)
    const lower = (msg || '').toLowerCase()
    if (lower.includes('quiz') || lower.includes('test me')) await sendMessage('Start a quiz for me', true, [], { action: 'start_quiz' })
  }

  const handleCustomMessage = async () => {
    if (!newMessage.trim() && selectedImages.length === 0) return

    if (user && messages.length === 0) {
      const { data: limitCheck, error: limitError } = await supabase.rpc('can_start_conversation', { p_user_id: user.id })
      if (!limitError && limitCheck && typeof limitCheck === 'object' && 'can_create' in limitCheck && !limitCheck.can_create) {
        setLimitInfo({ currentCount: (limitCheck as any).current_count, maxAllowed: (limitCheck as any).max_allowed })
        setShowUpsellModal(true)
        return
      }
    }

    const message = newMessage.trim()
    setNewMessage('')
    await sendMessage(message, true, selectedImages.length > 0 ? selectedImages : undefined)

    if (uploadedFiles.length > 0 && user && conversation) {
      try {
        await new Promise(r => setTimeout(r, 500))
        const { data: lastMessages } = await supabase.from('messages').select('*').eq('conversation_id', conversation.id).eq('role', 'user').order('created_at', { ascending: false }).limit(1)
        const lastMsg = lastMessages?.[0]
        if (lastMsg) {
          for (const f of uploadedFiles) {
            await saveAttachmentToDatabase(lastMsg.id, f, user.id, conversation.id)
            await addToUserFileLibrary(f, user.id, getSmartCategoryFromContext(f.type, category?.name, message), `Attachment from ${category?.name || 'general'} conversation`)
          }
        }
      } catch { }
    }
    setSelectedImages([]); setUploadedFiles([])
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCustomMessage() }
  }

  const handleStartOver = async () => { await sendMessage("I'd like to start over", true); setSelectedImages([]); setUploadedFiles([]) }
  const handleStartQuiz = async () => { await sendMessage('Start a quiz for me', true, [], { action: 'start_quiz' }) }

  const handleFileSelect = async (files: FileList) => {
    const uploads: typeof uploadedFiles = []
    for (const file of Array.from(files).slice(0, 4)) {
      if (file.size > 10 * 1024 * 1024) { alert(`${file.name} too large (max 10MB)`); continue }
      if (!user || !conversation) continue
      try {
        const f = await uploadFileToStorage(file, user.id, conversation.id, category?.name)
        uploads.push({ url: f.url, path: f.path, name: f.name, type: f.type, size: f.size })
      } catch { alert(`Failed to upload ${file.name}`) }
    }
    if (uploads.length) {
      setSelectedImages(p => [...p, ...uploads.map(f => f.url)].slice(0, 4))
      setUploadedFiles(p => [...p, ...uploads].slice(0, 4))
    }
  }

  const handleCameraCapture = async (files: FileList) => {
    const file = files[0]; if (!file || !user || !conversation) return
    if (file.size > 10 * 1024 * 1024) { alert('Photo too large (max 10MB)'); return }
    try {
      const f = await uploadFileToStorage(file, user.id, conversation.id, category?.name)
      setSelectedImages(p => [...p, f.url].slice(0, 4))
      setUploadedFiles(p => [...p, { url: f.url, path: f.path, name: f.name, type: f.type, size: f.size }].slice(0, 4))
    } catch { alert('Failed to upload photo') }
  }

  const handleRemoveImage = (i: number) => setSelectedImages(p => p.filter((_, idx) => idx !== i))
  const handleRemoveFile = (i: number) => setUploadedFiles(p => p.filter((_, idx) => idx !== i))

  // ─── Loading state ────────────────────────────────────────────────────
  const initialLoading = authLoading || isCategoryLoading || (isLoading && messages.length === 0 && !category)

  if (initialLoading) {
    return (
      <div style={{ minHeight: '100dvh', background: S.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '20px' }}>
        <div style={{
          width: '72px', height: '72px',
          background: `linear-gradient(135deg, ${S.sage} 0%, ${S.sageDark} 100%)`,
          borderRadius: '20px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '34px',
          boxShadow: '0 8px 32px rgba(74,124,94,0.30)',
          animation: 'sageFloat 3s ease-in-out infinite',
        }}><img src="/icons/blue-sage-logo.png" alt="Sage" style={{ width: '42px', height: '42px', objectFit: 'contain' }} /></div>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: S.fontDisplay, fontSize: '22px', fontWeight: 700, color: S.navy, marginBottom: '6px' }}>
            {category?.name || 'Starting your conversation…'}
          </h2>
          <p style={{ fontSize: '15px', color: S.slate }}>Sage is getting ready for you</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {[0, 0.15, 0.3].map((delay, i) => (
            <div key={i} style={{
              width: '10px', height: '10px', borderRadius: '50%',
              background: S.sage, opacity: 0.7,
              animation: `bounceDot 1.2s ease-in-out ${delay}s infinite`,
            }} />
          ))}
        </div>
        <style>{`
          @keyframes sageFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
          @keyframes bounceDot { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-8px)} }
        `}</style>
      </div>
    )
  }

  // ─── Error state ──────────────────────────────────────────────────────
  if (!isCategoryLoading && (error || !category)) {
    return (
      <div style={{ minHeight: '100dvh', background: S.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{
          maxWidth: '420px', width: '100%', background: S.white,
          borderRadius: '24px', padding: '40px 32px', textAlign: 'center',
          border: `1px solid ${S.creamDark}`, boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
        }}>
          <div style={{ width: '64px', height: '64px', margin: '0 auto 16px' }}><img src="/icons/blue-sage-logo.png" alt="Sage" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /></div>
          <h1 style={{ fontFamily: S.fontDisplay, fontSize: '22px', fontWeight: 700, color: S.navy, marginBottom: '10px' }}>Category Not Found</h1>
          <p style={{ fontSize: '15px', color: S.slate, marginBottom: '24px' }}>{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ width: '100%', padding: '14px 28px', background: S.sage, color: S.white, border: 'none', borderRadius: '14px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: S.fontBody }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // ─── Phase 5.2: Education choice screen ──────────────────────────────
  // Shown when a non-student visits an is_educational category.
  // Two CTAs: "Start Learning Journey" (→ /learn) or "Explore Casually" (→ companion).
  if (showEduChoice && category?.is_educational) {
    const slugifyCategory = (name: string) => name.toLowerCase().replace(/&/g, 'and').replace(/[\/]+/g, '-').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')

    const onExploreCasually = () => {
      navigate(`/category/${slugifyCategory(category?.name || '')}?mode=casual`, { replace: true })
    }

    return (
      <div style={{ minHeight: '100dvh', background: S.cream, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{
          maxWidth: '480px', width: '100%', background: S.white,
          borderRadius: '28px', overflow: 'hidden',
          boxShadow: '0 16px 64px rgba(26,39,64,0.12)',
          border: `1px solid ${S.creamDark}`,
        }}>
          {/* Header strip */}
          <div style={{
            background: `linear-gradient(135deg, ${S.navy} 0%, #243452 100%)`,
            padding: '28px 28px 20px',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '10px' }}>{category.icon}</div>
            <h1 style={{ fontFamily: S.fontDisplay, fontSize: '22px', fontWeight: 800, color: S.white, margin: 0 }}>
              {category.name}
            </h1>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.75)', marginTop: '6px' }}>
              {category.description}
            </p>
          </div>

          {/* Body */}
          <div style={{ padding: '28px' }}>
            <p style={{ fontSize: '15px', color: S.slate, lineHeight: 1.7, marginBottom: '24px' }}>
              {t('education.categoryChoice.body',
                'This is a structured educational subject. You can follow a guided learning journey with lessons, progress tracking, and certificates — or explore it casually with Sage.'
              )}
            </p>

            {/* CTA: Learning Journey */}
            <button
              onClick={() => navigate(`/learn?category=${category.id}`)}
              style={{
                width: '100%', padding: '16px 20px', borderRadius: '16px', border: 'none',
                background: `linear-gradient(135deg, ${S.studyBlue}, #4f6ee8)`,
                color: S.white, fontFamily: S.fontBody, fontSize: '16px', fontWeight: 700,
                cursor: 'pointer', marginBottom: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                boxShadow: '0 4px 18px rgba(59,91,219,0.3)',
              }}
            >
              <BookOpen size={20} />
              {t('education.categoryChoice.startJourney', '📚 Start Learning Journey')}
            </button>

            {/* CTA: Casual explore */}
            <button
              onClick={onExploreCasually}
              style={{
                width: '100%', padding: '14px 20px', borderRadius: '16px',
                border: `2px solid ${S.creamDark}`,
                background: S.white, color: S.slate,
                fontFamily: S.fontBody, fontSize: '15px', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('education.categoryChoice.exploreCasually', 'Explore Casually with Sage')}
            </button>

            <p style={{ fontSize: '12px', color: S.slate, opacity: 0.5, textAlign: 'center', marginTop: '16px' }}>
              {t('education.categoryChoice.hint', 'Learning Journey tracks your progress and unlocks certificates.')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <ConversationsSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        onConversationSelect={(cid) => navigate(`/conversation/${cid}`)}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
        {/* ── Header ── */}
        <header style={{
          background: S.white, borderBottom: `1px solid ${S.creamDark}`,
          boxShadow: '0 1px 8px rgba(0,0,0,0.04)', flexShrink: 0, zIndex: 30,
        }}>
          <div style={{ maxWidth: '896px', margin: '0 auto', padding: '0 16px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', cursor: 'pointer', color: S.slate,
                  transition: 'background 0.2s'
                }}
                className="hover:bg-gray-100"
              >
                <Menu size={20} />
              </button>

              <div style={{
                width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                background: `linear-gradient(135deg, ${S.sage} 0%, ${S.sageDark} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px',
                boxShadow: '0 3px 10px rgba(74,124,94,0.25)',
              }}>{category?.icon}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: S.fontDisplay, fontSize: '16px', fontWeight: 700, color: S.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {category?.name}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#22c55e' }} />
                  <span style={{ fontSize: '11px', color: S.sage }}>Ready to chat</span>
                </div>
              </div>
            </div>

            {/* Right: controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              <LanguagePicker />
              {category?.is_educational && (
                <button
                  onClick={() => navigate(`/learn?category=${category.id}`)}
                  style={{
                    padding: '8px 12px', borderRadius: '10px',
                    border: `1px solid ${S.studyBlue}20`,
                    background: `${S.studyBlue}10`, color: S.studyBlue,
                    fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    fontFamily: S.fontBody, display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  <BookOpen size={13} />
                  <span className="hidden sm:inline">{t('education.switchToLearning', 'Learning Mode')}</span>
                </button>
              )}
              <button
                onClick={handleStartQuiz}
                disabled={isLoading}
                style={{ padding: '8px 12px', borderRadius: '10px', border: `1px solid ${S.creamDark}`, background: S.cream, color: S.slate, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: S.fontBody, display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <span>📝</span><span className="hidden sm:inline">Quiz</span>
              </button>
              <button
                onClick={handleStartOver}
                disabled={isLoading}
                style={{ padding: '8px 12px', borderRadius: '10px', border: `1px solid ${S.creamDark}`, background: S.cream, color: S.slate, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: S.fontBody }}
              >↺ <span className="hidden sm:inline">Restart</span></button>
              {hasCompletedTour && <HelpButton onClick={restartTour} />}
            </div>
          </div>
        </header>

        {/* ── Messages ── */}
        <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain' }} className="sage-chat-messages">
          <div style={{ maxWidth: '896px', margin: '0 auto', padding: '24px 16px 24px' }}>
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{
                  width: '72px', height: '72px', margin: '0 auto 20px',
                  background: `linear-gradient(135deg, ${S.sage} 0%, ${S.sageDark} 100%)`,
                  borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '36px', boxShadow: '0 8px 32px rgba(74,124,94,0.25)',
                }}>{category?.icon}</div>
                <h2 style={{ fontFamily: S.fontDisplay, fontSize: '24px', fontWeight: 700, color: S.navy, marginBottom: '10px' }}>
                  Welcome to {category?.name}
                </h2>
                <p style={{ fontSize: '17px', color: S.slate, maxWidth: '480px', margin: '0 auto', lineHeight: 1.6 }}>
                  {t('category.startConversation')}
                </p>
              </div>
            )}

            {messages.map((message, index) => (
              <div key={message.id} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  {message.role === 'assistant' && (
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                      background: S.sageDark, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginRight: '10px', marginTop: '2px',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" />
                        <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
                      </svg>
                    </div>
                  )}

                  <div style={{
                    maxWidth: 'min(80%, 640px)', padding: '14px 18px',
                    borderRadius: message.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                    background: message.role === 'user' ? S.sage : S.white,
                    color: message.role === 'user' ? S.white : S.navy,
                    border: message.role === 'assistant' ? `1px solid ${S.creamDark}` : 'none',
                    boxShadow: message.role === 'user' ? '0 4px 14px rgba(74,124,94,0.22)' : '0 2px 12px rgba(0,0,0,0.05)',
                    position: 'relative',
                  }}>
                    {message.role === 'assistant' && (
                      <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', gap: '4px' }}>
                        <button onClick={async () => { try { await navigator.clipboard.writeText(message.content) } catch { } }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: S.sage, padding: '2px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        </button>
                      </div>
                    )}

                    {message.role === 'assistant' ? (
                      <div style={{ fontSize: '16px', lineHeight: 1.7, color: S.navy }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ node, ...p }: any) => <p {...p} style={{ fontSize: '16px', lineHeight: 1.7, marginBottom: '0.7em', color: S.navy }} />,
                            li: ({ node, ...p }: any) => <li {...p} style={{ fontSize: '16px', lineHeight: 1.65, color: S.navy }} />,
                            a: ({ node, ...p }: any) => <a {...p} style={{ color: S.sage, textDecoration: 'underline' }} target="_blank" rel="noreferrer" />,
                            strong: ({ node, ...p }: any) => <strong {...p} style={{ fontWeight: 700 }} />,
                            ul: ({ node, ...p }: any) => <ul {...p} style={{ paddingLeft: '1.2em', marginBottom: '0.7em' }} />,
                            ol: ({ node, ...p }: any) => <ol {...p} style={{ paddingLeft: '1.2em', marginBottom: '0.7em' }} />,
                          }}>
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p style={{ fontSize: '16px', lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0 }}>
                        {message.content.trim().startsWith('{') && message.content.trim().endsWith('}') ? '…' : message.content}
                      </p>
                    )}

                    {message.role === 'assistant' && index === messages.length - 1 && productResults.length > 0 && (
                      <ProductResults products={productResults} />
                    )}

                    <div style={{ fontSize: '11px', marginTop: '6px', color: message.role === 'user' ? 'rgba(255,255,255,0.55)' : S.sage, opacity: 0.8 }}>
                      {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>

                  {message.role === 'user' && (
                    <div style={{
                      width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0,
                      background: S.gold, color: S.white, fontWeight: 700, fontSize: '13px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginLeft: '10px', alignSelf: 'flex-end',
                      boxShadow: '0 2px 8px rgba(201,144,58,0.28)',
                    }}>
                      {user?.email?.[0].toUpperCase() || 'U'}
                    </div>
                  )}
                </div>

                {(() => {
                  const fups = (message.metadata as any)?.followUpOptions || []
                  return message.role === 'assistant' && index === messages.length - 1 && fups.length > 0 && (
                    <div style={{ marginLeft: '44px', marginTop: '12px' }}>
                      <FollowUpButtons options={fups} onSelect={handleFollowUpSelect} category={category?.name || 'general'} isLoading={isLoading} />
                    </div>
                  )
                })()}
              </div>
            ))}

            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: S.sageDark, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
                  </svg>
                </div>
                <div style={{ padding: '14px 18px', borderRadius: '20px 20px 20px 4px', background: S.white, border: `1px solid ${S.creamDark}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {[0, 0.15, 0.3].map((d, i) => <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: S.sage, animation: `bounceDot 1.2s ease-in-out ${d}s infinite` }} />)}
                    </div>
                    <span style={{ fontSize: '13px', color: S.sage }}>Sage is thinking…</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ── Voice Control Modal ── */}
        {showVoiceControl && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowVoiceControl(false)} />
            <div style={{ position: 'relative', background: S.white, borderRadius: '20px', padding: '20px', maxWidth: '360px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
              <div style={{ fontWeight: 700, color: S.navy, marginBottom: '8px' }}>Voice Controls</div>
              <div style={{ fontSize: '14px', color: S.slate, marginBottom: '16px' }}>
                {isVoiceListening ? 'Listening…' : isVoiceSpeaking ? 'AI speaking…' : 'Connected'}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={stopVoiceSession} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#ef4444', color: S.white, fontWeight: 700, cursor: 'pointer' }}>Pause</button>
                <button onClick={toggleMute} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: `1px solid ${S.creamDark}`, background: S.cream, color: S.navy, fontWeight: 700, cursor: 'pointer' }}>{isMuted ? 'Unmute' : 'Mute'}</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Input area ── */}
        <div style={{ flexShrink: 0, background: S.white, borderTop: `1px solid ${S.creamDark}`, padding: '10px 12px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))' }}>
          <div style={{ maxWidth: '896px', margin: '0 auto' }}>

            {(() => {
              if (voiceFollowUpOptions.length > 0) return (
                <div style={{ marginBottom: '8px' }}>
                  <FollowUpButtons options={voiceFollowUpOptions} onSelect={handleFollowUpSelect} isLoading={isLoading} allowCustomInput={false} category={category?.name || ''} />
                </div>
              )
              const last = [...messages].reverse().find(m => m.role === 'assistant')
              const fups = (last?.metadata as any)?.followUpOptions || []
              return fups.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  <FollowUpButtons options={fups} onSelect={handleFollowUpSelect} isLoading={isLoading} allowCustomInput={false} category={category?.name || ''} />
                </div>
              )
            })()}

            {(currentTranscript || (voiceError && voiceError !== 'No conversation ID available')) && (
              <div style={{ marginBottom: '8px', padding: '10px 14px', borderRadius: '12px', border: `1px solid ${S.creamDark}`, background: '#f8f9fa' }}>
                {currentTranscript && <div style={{ fontSize: '13px', color: S.navy }}><span style={{ fontWeight: 600, color: S.sage }}>🎤 {isVoiceListening ? 'Listening…' : 'Voice active'}</span><div style={{ marginTop: '4px' }}>{currentTranscript}</div></div>}
                {voiceError && voiceError !== 'No conversation ID available' && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', color: '#ef4444' }}>
                    <span>{voiceError}</span>
                    <button onClick={clearVoiceError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>✕</button>
                  </div>
                )}
              </div>
            )}

            <div style={{ background: S.cream, border: `2px solid ${S.creamDark}`, borderRadius: '18px', padding: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '4px' }}>
                <button
                  onClick={isVoiceSpeaking ? stopVoiceSession : toggleVoiceSession}
                  disabled={isLoading}
                  style={{
                    width: '44px', height: '44px', flexShrink: 0, borderRadius: '12px',
                    border: isVoiceListening ? 'none' : `2px solid ${S.sageMid}`,
                    background: isVoiceListening ? '#ef4444' : S.sageLight,
                    color: isVoiceListening ? S.white : S.sage,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {isVoiceSpeaking ? <VolumeX size={18} color="#ef4444" /> : <Mic size={18} className={isVoiceListening ? 'animate-pulse' : ''} />}
                </button>

                <textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={e => { setNewMessage(e.target.value); adjustTextareaHeight() }}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message, or press the microphone to speak…"
                  disabled={isLoading}
                  rows={1}
                  style={{
                    flex: 1, minHeight: '28px', maxHeight: '120px',
                    resize: 'none', border: 'none', outline: 'none',
                    background: 'transparent', padding: '8px 0',
                    fontSize: '16px', color: S.navy, lineHeight: 1.5,
                    fontFamily: S.fontBody,
                  }}
                />

                <button onClick={() => setShowAttachments(true)}
                  style={{ padding: '8px', borderRadius: '10px', border: 'none', background: 'none', cursor: 'pointer', color: `${S.slate}88`, flexShrink: 0 }}>
                  <Paperclip size={18} />
                </button>

                <button
                  onClick={handleCustomMessage}
                  disabled={(!newMessage.trim() && selectedImages.length === 0) || isLoading}
                  style={{
                    height: '44px', padding: '0 18px', flexShrink: 0,
                    borderRadius: '12px', border: 'none',
                    background: S.sage, color: S.white,
                    fontFamily: S.fontBody, fontSize: '15px', fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                    boxShadow: '0 3px 10px rgba(74,124,94,0.28)',
                    opacity: (!newMessage.trim() && selectedImages.length === 0) || isLoading ? 0.5 : 1,
                  }}
                >Send <Send size={15} /></button>
              </div>

              {selectedImages.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '8px 10px 4px', borderTop: `1px solid ${S.creamDark}`, marginTop: '6px' }}>
                  {selectedImages.map((src, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={src} alt="" style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '10px', border: `1px solid ${S.creamDark}` }} />
                      <button onClick={() => { handleRemoveImage(i); handleRemoveFile(i) }}
                        style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', background: S.white, border: '1px solid #ddd', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p style={{ fontSize: '11px', color: S.slate, opacity: 0.4, textAlign: 'center', marginTop: '8px' }}>
              Press Enter to send · Shift+Enter for new line
            </p>
          </div>
        </div>

        {/* ── Modals ── */}
        {limitInfo && <SubscriptionUpsellModal isOpen={showUpsellModal} onClose={() => setShowUpsellModal(false)} currentCount={limitInfo.currentCount} maxAllowed={limitInfo.maxAllowed} />}
        <QuizModal isOpen={isQuizOpen} quiz={quiz} onClose={() => setIsQuizOpen(false)} onSubmit={submitQuizResults} />
        <VoiceSettingsModal isOpen={showVoiceSettings} onClose={() => setShowVoiceSettings(false)} isVoiceListening={isVoiceListening} onVoiceToggle={toggleVoiceSession} isVoiceSpeaking={isVoiceSpeaking} onStopSpeaking={stopVoiceSession} currentTranscript={currentTranscript} voiceError={voiceError} onClearVoiceError={clearVoiceError} realtimeVoice={realtimeVoice} onRealtimeVoiceChange={handleRealtimeVoiceChange} isMuted={isMuted} onToggleMute={toggleMute} />
        <AttachmentsModal isOpen={showAttachments} onClose={() => setShowAttachments(false)} onFileSelect={handleFileSelect} onCameraCapture={handleCameraCapture} selectedImages={selectedImages} onRemoveImage={handleRemoveImage} uploadedFiles={uploadedFiles} onRemoveFile={handleRemoveFile} />
        {showTour && currentStepData && <OnboardingTour currentStep={currentStepData} stepIndex={currentStep} totalSteps={totalSteps} onNext={nextStep} onPrevious={previousStep} onSkip={skipTour} />}

        <style>{`
          @keyframes bounceDot { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-8px)} }
        `}</style>
      </div>
    </div>
  )
}